import type { TruckTrailerCoupling } from '@chukua/common';
import { Alert, DatePicker, Form, Modal } from 'antd';
import dayjs, { type Dayjs } from 'dayjs';
import { useState } from 'react';
import { useToast } from '../../../shared/hooks/use-toast';
import { apiErrorMessage } from '../../../shared/policies/api-error-message';
import { ZoneSearchSelect } from '../../zones/public/ZoneSearchSelect';
import { uncoupleTrailer } from '../features/units/units.api';

interface UncoupleTrailerModalProps {
  coupling: { id: string; truckRegistration: string; trailerRegistration: string };
  tripNotice?: string;
  onClose: () => void;
  onUncoupled: (coupling: TruckTrailerCoupling) => void;
}

/** Drop a trailer (gitaumoses4/chukua#390): where it is left is a zone, so a parked trailer can be found again. */
export function UncoupleTrailerModal({ coupling, tripNotice, onClose, onUncoupled }: UncoupleTrailerModalProps) {
  const toast = useToast();
  const [dropZoneCode, setDropZoneCode] = useState<string | undefined>(undefined);
  const [uncoupledAt, setUncoupledAt] = useState<Dayjs | null>(null);
  const [error, setError] = useState<string | undefined>(undefined);
  const [submitting, setSubmitting] = useState(false);

  async function submit() {
    if (!dropZoneCode) {
      setError('Choose where the trailer is dropped.');
      return;
    }
    setSubmitting(true);
    setError(undefined);
    try {
      const ended = await uncoupleTrailer(coupling.id, { dropZoneCode, uncoupledAt: uncoupledAt?.toISOString() });
      toast.success(`${coupling.trailerRegistration} uncoupled from ${coupling.truckRegistration}`);
      onUncoupled(ended);
      onClose();
    } catch (err) {
      setError(apiErrorMessage(err) ?? "Couldn't uncouple the trailer. Try again.");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Modal
      open
      title={`Uncouple ${coupling.trailerRegistration} from ${coupling.truckRegistration}?`}
      okText="Uncouple"
      confirmLoading={submitting}
      onOk={submit}
      onCancel={onClose}
      destroyOnHidden
    >
      <Form layout="vertical" requiredMark={false}>
        {tripNotice && <Alert className="mb-4" type="info" showIcon message={tripNotice} />}
        <Form.Item label="Dropped at" htmlFor="uncouple-drop-zone" required>
          <ZoneSearchSelect id="uncouple-drop-zone" value={dropZoneCode} onChange={setDropZoneCode} />
        </Form.Item>
        <Form.Item label="Uncoupled at" htmlFor="uncouple-at" extra="Leave empty for now.">
          <DatePicker
            id="uncouple-at"
            className="w-full"
            showTime={{ format: 'HH:mm' }}
            format="D MMM YYYY, HH:mm"
            value={uncoupledAt}
            onChange={setUncoupledAt}
            disabledDate={(day) => day.isAfter(dayjs(), 'day')}
          />
        </Form.Item>
        {error && <Alert type="error" showIcon message={error} />}
      </Form>
    </Modal>
  );
}
